import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TOKEN_STORAGE_KEY } from '../../shared/constant/constant';
import {
    getFromStorage,
    removeFromStorage,
    saveToStorage,
} from '../../shared/helpers/localStorage';
import { AuthState, SocialPage, UserAttributes, UserData } from '../../shared/types/authTypes';
import {
    fetchCurrentUser,
    fetchUserProfile,
    forgotPassword,
    login,
    logout,
    register,
    resetPassword,
    updateUserProfile,
    updateUserBio,
    confirmEmailChange,
    requestEmailChange,
    uploadProfilePhoto,
    removeProfilePhoto,
} from '../../shared/api/authApi';
import { fetchTempToken } from '../../shared/api/authApi';

const updateFirstConnectedSocial = (state: AuthState, user: any) => {
    if (user?.social_connections) {
        const connections =
            typeof user.social_connections === 'string'
                ? JSON.parse(user.social_connections)
                : user.social_connections;

        state.firstConnectedSocial =
            Object.entries(connections).find(
                ([key, value]) => value !== null && value !== false,
            )?.[0] || null;
    } else {
        state.firstConnectedSocial = null;
    }
};

const storedToken = getFromStorage(TOKEN_STORAGE_KEY);
console.log('storedToken', storedToken);
export const initialState: AuthState = {
    token: storedToken,
    errors: null,
    isLoading: false,
    isUpdating: false,
    accessToken: storedToken,
    isAuthenticated: !!storedToken,
    isEmailVerified: false,
    user: null,
    userProfile: null,
    firstConnectedSocial: null,
    socialPages: [
        {
            id: '1',
            type: 'facebook',
        },
        {
            id: '2',
            type: 'pinterest',
        },
    ],
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setToken: (state, action: PayloadAction<AuthState['token']>) => {
            state.token = action.payload;
            state.accessToken = action.payload;
            if (action.payload) {
                saveToStorage(TOKEN_STORAGE_KEY, action.payload);
            } else {
                removeFromStorage(TOKEN_STORAGE_KEY);
            }
        },
        setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
            state.isAuthenticated = action.payload;
        },
        setUser: (state, action: PayloadAction<UserAttributes>) => {
            state.user = action.payload;
        },
        setSocialPages: (state, action: PayloadAction<SocialPage[]>) => {
            state.socialPages = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTempToken.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(fetchTempToken.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                state.token = action.payload;
                state.accessToken = action.payload;
                saveToStorage(TOKEN_STORAGE_KEY, action.payload);
            })
            .addCase(fetchTempToken.rejected, (state, action) => {
                state.isLoading = false;
                // state.err]or = action.payload || 'Failed to fetch token';
            })
            .addCase(register.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(register.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
            })
            .addCase(register.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
                state.isAuthenticated = false;
                state.user = null;
                removeFromStorage(TOKEN_STORAGE_KEY);
            })
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(login.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
                state.isAuthenticated = false;
                state.user = null;
                removeFromStorage(TOKEN_STORAGE_KEY);
            })
            .addCase(logout.fulfilled, (state) => {
                state.token = null;
                state.accessToken = null;
                state.isAuthenticated = false;
                state.user = null;
                state.errors = null;
            })
            .addCase(fetchCurrentUser.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = {
                    ...action.payload.attributes,
                    social_connections: action.payload.meta.social_connections,
                    id: action.payload.id,
                };
                state.isAuthenticated = true;
                updateFirstConnectedSocial(state, state.user);
            })
            .addCase(fetchCurrentUser.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
                state.isAuthenticated = false;
                state.user = null;
                removeFromStorage(TOKEN_STORAGE_KEY);
            })
            .addCase(updateUserProfile.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userProfile = action.payload;
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
                state.isAuthenticated = false;
                state.user = null;
                removeFromStorage(TOKEN_STORAGE_KEY);
            })
            .addCase(fetchUserProfile.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })

            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userProfile = action.payload;
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
                state.isAuthenticated = false;
                state.userProfile = null;
            })
            .addCase(forgotPassword.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.errors = null;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
            })
            .addCase(resetPassword.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.errors = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload ? [action.payload] : null;
            })
            .addCase(updateUserBio.pending, (state) => {
                state.isUpdating = true;
                state.errors = null;
            })
            .addCase(updateUserBio.fulfilled, (state, action) => {
                state.isUpdating = false;
                if (state.user) {
                    state.user = {
                        ...state.user,
                        ...action.payload,
                    };
                } else {
                    state.user = action.payload;
                }
                updateFirstConnectedSocial(state, state.user);
            })
            .addCase(updateUserBio.rejected, (state, action) => {
                state.isUpdating = false;
                state.errors = action.payload ? [action.payload] : null;
            })
            .addCase(requestEmailChange.pending, (state) => {
                state.isUpdating = true;
                state.errors = null;
            })
            .addCase(requestEmailChange.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(requestEmailChange.rejected, (state, action) => {
                state.isUpdating = false;
                state.errors = action.payload ? [action.payload] : null;
            })
            .addCase(confirmEmailChange.pending, (state) => {
                state.isUpdating = true;
                state.errors = null;
            })
            .addCase(confirmEmailChange.fulfilled, (state, action) => {
                state.isUpdating = false;
                if (state.user) {
                    state.user = { ...state.user, ...action.payload };
                } else {
                    state.user = action.payload;
                }
            })
            .addCase(confirmEmailChange.rejected, (state, action) => {
                state.isUpdating = false;
                if (action.payload) {
                    state.errors = [action.payload as { [key: string]: string }];
                } else {
                    state.errors = null;
                }
            })
            .addCase(uploadProfilePhoto.pending, (state) => {
                state.isUpdating = true;
                state.errors = null;
            })
            .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
                state.isUpdating = false;
                state.errors = null;
                if (state.user) {
                    state.user = { ...state.user, ...action.payload };
                } else {
                    state.user = action.payload;
                }
            })
            .addCase(uploadProfilePhoto.rejected, (state, action) => {
                state.isUpdating = false;
                if (action.payload) {
                    state.errors = [action.payload as { [key: string]: string }];
                } else {
                    state.errors = null;
                }
            })
            .addCase(removeProfilePhoto.pending, (state) => {
                state.isUpdating = true;
                state.errors = null;
            })
            .addCase(removeProfilePhoto.fulfilled, (state, action) => {
                state.isUpdating = false;
                state.errors = null;
                if (state.user) {
                    state.user = { ...state.user, ...action.payload };
                } else {
                    state.user = action.payload;
                }
            })
            .addCase(removeProfilePhoto.rejected, (state, action) => {
                state.isUpdating = false;
                if (action.payload) {
                    state.errors = [action.payload as { [key: string]: string }];
                } else {
                    state.errors = null;
                }
            });
    },
});

export const { setToken, setIsAuthenticated, setUser, setSocialPages } = authSlice.actions;
export default authSlice.reducer;
